<template>
  <div>
    <Toolbar />
    <div class="container-xxl pb-10">
      <div v-if="!canView" class="alert alert-warning">
        Você não tem permissão para visualizar a assinatura deste local.
      </div>
      <template v-else-if="subscription">
        <div
          v-if="usesSelfServiceBilling && subscription.needs_payment_method"
          class="alert alert-warning d-flex align-items-center justify-content-between flex-wrap gap-2 mb-5"
        >
          <span>Configure a forma de pagamento antes do fim do trial para continuar usando o sistema.</span>
          <router-link v-if="canManage" to="/subscription/payment-method" class="btn btn-sm btn-primary">
            Configurar pagamento
          </router-link>
        </div>

        <div
          v-if="usesSelfServiceBilling && subscription.pending_plan"
          class="alert subscription-overview__pending-alert d-flex align-items-start gap-3 mb-5"
        >
          <i class="bi bi-arrow-repeat fs-3 text-primary mt-1"></i>
          <div>
            <p class="fw-bold mb-1">Troca de plano agendada</p>
            <p class="mb-0 text-gray-700">
              Você continua com os benefícios do <strong>{{ subscription.plan?.name }}</strong>
              até o próximo pagamento confirmado.
              Depois disso, seu plano passará para <strong>{{ subscription.pending_plan.name }}</strong>.
            </p>
          </div>
        </div>

        <div
          v-if="usesSelfServiceBilling && subscription.status === 'past_due' && subscription.overdue_charge"
          class="alert alert-danger d-flex align-items-center justify-content-between flex-wrap gap-2 mb-5"
        >
          <span>
            Há uma fatura em aberto (vencimento {{ formatDate(subscription.overdue_charge.charge_due_date) }}).
          </span>
          <router-link to="/subscription/overdue" class="btn btn-sm btn-danger">
            Regularizar pagamento
          </router-link>
        </div>

        <div class="card mb-5">
          <div class="card-header">
            <h3 class="card-title">Minha assinatura</h3>
            <div class="card-toolbar" v-if="canManage && usesSelfServiceBilling">
              <router-link class="btn btn-sm btn-light-primary me-2" to="/subscription/payment-method">
                Forma de pagamento
              </router-link>
              <router-link class="btn btn-sm btn-light-primary me-2" to="/subscription/plans">Trocar plano</router-link>
              <router-link class="btn btn-sm btn-light" to="/subscription/invoices">Faturas</router-link>
              <router-link class="btn btn-sm btn-light ms-2" to="/subscription/statement">Extrato</router-link>
            </div>
          </div>
          <div class="card-body">
            <div class="row g-4 mb-2">
              <div class="col-md-4">
                <div class="subscription-overview__stat">
                  <span class="subscription-overview__stat-label">Plano atual</span>
                  <span class="subscription-overview__stat-value">{{ subscription.plan?.name || '—' }}</span>
                  <span v-if="planPriceLabel" class="text-muted fs-7">{{ planPriceLabel }}</span>
                  <span v-if="billingBreakdownHint" class="text-muted fs-7 d-block">{{ billingBreakdownHint }}</span>
                </div>
              </div>
              <div class="col-md-4">
                <div class="subscription-overview__stat">
                  <span class="subscription-overview__stat-label">Status</span>
                  <span>
                    <span class="badge" :class="statusBadgeClass">{{ subscription.status_label }}</span>
                  </span>
                  <span v-if="isTrial && subscription.trial_ends_at" class="text-muted fs-7">
                    Trial até {{ formatDate(subscription.trial_ends_at) }}
                  </span>
                  <span v-else-if="subscription.current_period_end" class="text-muted fs-7">
                    Ciclo até {{ formatDate(subscription.current_period_end) }}
                  </span>
                </div>
              </div>
              <div class="col-md-4">
                <div class="subscription-overview__stat">
                  <span class="subscription-overview__stat-label">
                    {{ usesSelfServiceBilling ? 'Forma de pagamento' : 'Cobrança' }}
                  </span>
                  <span class="subscription-overview__stat-value">
                    {{
                      usesSelfServiceBilling
                        ? (subscription.payment_method_label || 'Não configurada')
                        : (subscription.billing_mode_label || 'Gerenciada comercialmente')
                    }}
                  </span>
                  <template v-if="usesSelfServiceBilling">
                    <span v-if="subscription.payment_method_details?.card" class="text-muted fs-7">
                      {{ subscription.payment_method_details.card.card_brand || 'Cartão' }}
                      final {{ subscription.payment_method_details.card.card_last_digits }}
                    </span>
                    <span v-else-if="subscription.payment_method === 'bank_slip'" class="text-muted fs-7">
                      Boleto enviado por e-mail todo dia 1, vencimento dia 10
                    </span>
                    <span v-else-if="subscription.payment_method === 'pix'" class="text-muted fs-7">
                      Cobrança via Pix conforme faturas emitidas
                    </span>
                  </template>
                  <span v-else class="text-muted fs-7">
                    Entre em contato com o comercial para alterações contratuais.
                  </span>
                </div>
              </div>
            </div>

            <div
              v-if="!subscription.subscribed_by"
              class="text-muted fs-7 mt-2 mb-0"
            >
              Responsável pela assinatura não registrado (cadastro anterior à funcionalidade).
            </div>
            <div v-else class="mt-3 pt-3 border-top">
              <span class="text-muted fs-7">Responsável pela assinatura:</span>
              <span class="fw-semibold ms-1">{{ subscription.subscribed_by.user_name }}</span>
              <span class="text-muted fs-7 ms-1">({{ subscription.subscribed_by.user_email }})</span>
            </div>
          </div>
        </div>

        <div
          v-if="subscription.whatsapp_quota && !subscription.whatsapp_quota.exempt"
          class="card mb-5"
        >
          <div class="card-header">
            <h3 class="card-title">Envios WhatsApp automáticos</h3>
          </div>
          <div class="card-body">
            <p v-if="subscription.pending_plan" class="text-muted fs-7 mb-3">
              Cota do plano atual (<strong>{{ subscription.plan?.name }}</strong>).
              Ao mudar para {{ subscription.pending_plan.name }}, a cota será ajustada no próximo ciclo.
            </p>

            <p v-if="subscription.whatsapp_quota.unlimited" class="mb-0">
              <i class="bi bi-infinity text-success me-2"></i>
              Envios automáticos ilimitados neste ciclo.
            </p>
            <template v-else>
              <div class="d-flex justify-content-between mb-1">
                <span class="text-muted fs-7">Uso no ciclo atual</span>
                <span class="fw-semibold fs-7">
                  {{ subscription.whatsapp_quota.used }} / {{ subscription.whatsapp_quota.included }}
                </span>
              </div>
              <div class="progress h-8px mb-2">
                <div
                  class="progress-bar"
                  :class="whatsappQuotaProgressClass"
                  role="progressbar"
                  :style="{ width: whatsappQuotaPercent + '%' }"
                ></div>
              </div>
              <p class="text-muted fs-7 mb-0" v-if="subscription.whatsapp_quota.extra_purchased > 0">
                Inclui {{ subscription.whatsapp_quota.extra_purchased }} envios extras comprados neste ciclo
              </p>
              <p class="text-muted fs-7 mb-0">
                {{ whatsappRenewalHint }}
                <span v-if="subscription.whatsapp_quota.period_end">
                  · Ciclo até {{ formatDate(subscription.whatsapp_quota.period_end) }}
                </span>
              </p>
              <p v-if="subscription.whatsapp_quota.remaining === 0" class="text-danger fs-7 mt-2 mb-0">
                Cota esgotada. Envios automáticos via WhatsApp pausados até a renovação ou compra de pacote.
              </p>
              <button
                v-if="usesSelfServiceBilling && canManage && subscription.whatsapp_quota.can_purchase_packages"
                type="button"
                class="btn btn-sm btn-light-primary mt-3"
                @click="openPackagePurchase"
              >
                Comprar envios extras
              </button>
            </template>
          </div>
        </div>

        <div
          v-if="subscription.plan_presentation || subscription.feature_groups?.length"
          class="card mb-5"
        >
          <div class="card-header">
            <h3 class="card-title">
              {{ subscription.pending_plan ? `Benefícios do plano atual (${subscription.plan?.name})` : 'O que está incluso' }}
            </h3>
          </div>
          <div class="card-body">
            <PlanOfferingPresentation
              v-if="subscription.plan_presentation"
              :presentation="subscription.plan_presentation"
              hide-callfy-quota
            />
            <template v-else>
              <div v-for="group in subscription.feature_groups" :key="group.slug" class="mb-4">
                <h6 class="fw-bold mb-2">{{ group.name }}</h6>
                <ul class="list-unstyled mb-0 ps-0">
                  <li v-for="feature in group.features" :key="feature.slug" class="d-flex align-items-start mb-2">
                    <i class="bi bi-check-circle-fill text-success me-2 mt-1"></i>
                    <div>
                      <span class="fw-semibold">{{ feature.name }}</span>
                      <span v-if="feature.description" class="d-block text-muted fs-7">{{ feature.description }}</span>
                    </div>
                  </li>
                </ul>
              </div>
            </template>
          </div>
        </div>

        <div
          v-if="usesSelfServiceBilling && subscription.pending_plan?.presentation"
          class="card mb-5 subscription-overview__pending-card"
        >
          <div class="card-header">
            <h3 class="card-title mb-0">
              <i class="bi bi-calendar-event me-2 text-primary"></i>
              A partir do próximo pagamento: {{ subscription.pending_plan.name }}
            </h3>
          </div>
          <div class="card-body">
            <p class="text-muted fs-7 mb-4">
              Prévia do que passará a valer quando a troca for confirmada.
            </p>
            <PlanOfferingPresentation :presentation="subscription.pending_plan.presentation" />
          </div>
        </div>

        <div class="card" v-if="subscription.place_users?.length">
          <div class="card-header">
            <h3 class="card-title">Usuários do local</h3>
          </div>
          <div class="card-body table-responsive">
            <table class="table table-row-bordered align-middle">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>E-mail</th>
                  <th>Regra de acesso</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="user in subscription.place_users" :key="user.id_user">
                  <td>{{ user.user_name }}</td>
                  <td>{{ user.user_email }}</td>
                  <td>{{ user.access_type_name }}</td>
                  <td>
                    <span v-if="user.is_subscribed_by" class="badge badge-light-success">Responsável pela assinatura</span>
                    <span v-else-if="user.is_administrator" class="badge badge-light-primary">Administrador</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </template>

      <div v-else class="card">
        <div class="card-body">
          <span class="text-muted">Carregando...</span>
        </div>
      </div>
    </div>

    <WhatsAppPackagePurchaseModal
      ref="packageModal"
      @purchased="onPackagePurchased"
    />
    <PlatformSubscriptionPayChargeModal
      :charge="pendingCharge"
      :has-saved-card="hasSavedCard"
      @paid="onChargePaid"
      @close="pendingCharge = null"
    />
  </div>
</template>

<script>
import Toolbar from '../../components/layouts/auth/Toolbar.vue';
import PlanOfferingPresentation from '../../components/platform_subscription/PlanOfferingPresentation.vue';
import WhatsAppPackagePurchaseModal from './WhatsAppPackagePurchaseModal.vue';
import PlatformSubscriptionPayChargeModal from './PlatformSubscriptionPayChargeModal.vue';
import http from '../../helpers/http';
import Permission from '../../helpers/permission';
import { usesSelfServiceBilling } from '../../helpers/platformSubscription';
import moment from 'moment';

export default {
  name: 'SubscriptionOverview',
  components: {
    Toolbar,
    PlanOfferingPresentation,
    WhatsAppPackagePurchaseModal,
    PlatformSubscriptionPayChargeModal,
  },
  data() {
    return {
      subscription: null,
      pendingCharge: null,
    };
  },
  computed: {
    canView() {
      return Permission.has('View Place Platform Subscription');
    },
    canManage() {
      return Permission.has('Manage Place Platform Subscription');
    },
    usesSelfServiceBilling() {
      return usesSelfServiceBilling();
    },
    isTrial() {
      return this.subscription?.status === 'trial';
    },
    planPriceLabel() {
      const plan = this.subscription?.plan;
      if (!plan || plan.slug === 'full-free') {
        return null;
      }

      if (this.subscription?.billing_breakdown?.uses_custom_monthly_price) {
        const total = this.subscription.estimated_monthly_total;
        if (!total || Number(total) <= 0) {
          return 'Valor sob consulta';
        }

        return `R$ ${Number(total).toFixed(2).replace('.', ',')}/mês (contrato)`;
      }

      const total = this.subscription?.estimated_monthly_total;
      const base = plan.price;

      if ((!total || Number(total) <= 0) && (!base || Number(base) <= 0)) {
        return 'Valor sob consulta';
      }

      if (total && Number(total) > 0) {
        return `R$ ${Number(total).toFixed(2).replace('.', ',')}/mês`;
      }

      return `R$ ${Number(base).toFixed(2).replace('.', ',')}/mês`;
    },
    billingBreakdownHint() {
      const breakdown = this.subscription?.billing_breakdown;
      if (!breakdown || breakdown.uses_custom_monthly_price) {
        return null;
      }

      const active = breakdown.active_professionals ?? 0;
      const included = breakdown.included_professionals ?? 0;
      const extras = breakdown.billable_extra_professionals ?? 0;
      const unit = breakdown.extra_professional_unit_price;

      if (extras <= 0) {
        return `${active} profissional(is) ativo(s) · ${included} incluído(s) no plano`;
      }

      const unitLabel = unit != null
        ? `R$ ${Number(unit).toFixed(2).replace('.', ',')}`
        : '—';

      return `${active} ativo(s) · ${included} incluído(s) · ${extras} extra(s) × ${unitLabel}`;
    },
    statusBadgeClass() {
      const map = {
        trial: 'badge-light-warning',
        active: 'badge-light-success',
        past_due: 'badge-light-danger',
        suspended: 'badge-light-dark',
        canceled: 'badge-light-secondary',
      };

      return map[this.subscription?.status] || 'badge-light-primary';
    },
    whatsappRenewalHint() {
      if (this.isTrial) {
        return 'Renova ao confirmar o primeiro pagamento';
      }

      return 'Renova ao confirmar o pagamento da mensalidade';
    },
    whatsappQuotaPercent() {
      const quota = this.subscription?.whatsapp_quota;
      if (!quota || quota.unlimited || quota.included == null) {
        return 0;
      }

      if (quota.included === 0) {
        return quota.used > 0 ? 100 : 0;
      }

      return Math.min(100, Math.round((quota.used / quota.included) * 100));
    },
    whatsappQuotaProgressClass() {
      if (this.whatsappQuotaPercent >= 90) {
        return 'bg-danger';
      }

      if (this.whatsappQuotaPercent >= 70) {
        return 'bg-warning';
      }

      return 'bg-success';
    },
    hasSavedCard() {
      return Boolean(this.subscription?.payment_method_details?.card);
    },
  },
  created() {
    if (this.canView) {
      this.load();
    }
  },
  methods: {
    load() {
      const placeId = this.$store.state.auth.place?.id_place;
      if (!placeId) {
        return;
      }

      http.get(`/places/${placeId}/platform-subscription`).then((res) => {
        this.subscription = res.data;
        this.$store.commit('auth/setPlatformSubscription', res.data);
      });
    },
    formatDate(value) {
      return moment(value).format('DD/MM/YYYY');
    },
    openPackagePurchase() {
      this.$refs.packageModal?.open();
    },
    onPackagePurchased(charge) {
      this.pendingCharge = charge;
    },
    onChargePaid() {
      this.pendingCharge = null;
      this.load();
    },
  },
};
</script>

<style scoped>
.subscription-overview__stat {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

.subscription-overview__stat-label {
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #a1a5b7;
}

.subscription-overview__stat-value {
  font-size: 1.1rem;
  font-weight: 700;
  color: #181c32;
}

.subscription-overview__pending-alert {
  background: #f1faff;
  border: 1px solid #b5e4ff;
  color: #181c32;
}

.subscription-overview__pending-card {
  border: 1px dashed #b5b5c3;
  background: #fafafa;
}
</style>
